import React, { Suspense, lazy, useCallback, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import IntroLoader from "./components/IntroLoader";
import Header from "./components/Header";
import PalaceHeroLite from "./components/PalaceHeroLite";
import LeadershipSpotlight from "./components/LeadershipSpotlight";
import ArtistSpotlight from "./components/ArtistSpotlight";
import Collection from "./components/Collection";
import Bookings from "./components/Bookings";
import BookingQuickPrompt from "./components/BookingQuickPrompt";
import SiteSoundtrack from "./components/SiteSoundtrack";
import WhatsAppFab from "./components/WhatsAppFab";
import SitePaused from "./components/SitePaused";
import { useVisibilityMount } from "./hooks/useVisibilityMount";
import { useMobileSafeMode } from "./hooks/useMobileSafeMode";

const PalaceHero = lazy(() => import("./components/PalaceHero"));
const CaseStudies = lazy(() => import("./components/CaseStudies"));
const RubhaaiSocial = lazy(() => import("./components/RubhaaiSocial"));
const ProductionDesign = lazy(() => import("./components/ProductionDesign"));
const VenueStageShowcase = lazy(() => import("./components/VenueStageShowcase"));
const HotelCollaborations = lazy(() => import("./components/HotelCollaborations"));
const PerformanceTimeline = lazy(() => import("./components/PerformanceTimeline"));
const Worldwide = lazy(() => import("./components/Worldwide"));
const Gallery = lazy(() => import("./components/Gallery"));
const Location = lazy(() => import("./components/Location"));
const Footer = lazy(() => import("./components/Footer"));

const SITE_PAUSED = true;

const SectionFallback = ({ minHeight = 320 }) => (
  <div
    className="relative overflow-hidden bg-[#0a0a0a]"
    style={{ minHeight }}
    aria-hidden="true"
  >
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.08),transparent_62%)]" />
    <div className="absolute inset-x-[10%] top-1/2 h-px -translate-y-1/2 bg-[linear-gradient(90deg,transparent,rgba(212,175,55,0.35),transparent)]" />
  </div>
);

const DeferredSection = ({
  component: Component,
  minHeight,
  rootMargin = "720px 0px",
  anchorId,
}) => {
  const [ref, shouldRender] = useVisibilityMount({ rootMargin, once: true });

  return (
    <div
      ref={ref}
      id={anchorId}
      className={anchorId ? "scroll-mt-28" : undefined}
    >
      {shouldRender ? (
        <Suspense fallback={<SectionFallback minHeight={minHeight} />}>
          <Component />
        </Suspense>
      ) : (
        <SectionFallback minHeight={minHeight} />
      )}
    </div>
  );
};

const Landing = () => {
  const mobileSafeMode = useMobileSafeMode();
  const [loaded, setLoaded] = useState(false);
  const [soundtrackStartCue, setSoundtrackStartCue] = useState(false);
  const [soundtrackBloomCue, setSoundtrackBloomCue] = useState(false);
  const [bookingPromptOpen, setBookingPromptOpen] = useState(false);
  const handleIntroDone = useCallback(() => {
    setLoaded(true);
  }, []);
  const handleIntroOpening = useCallback(() => {
    setSoundtrackStartCue(true);
  }, []);
  const handleIntroReveal = useCallback(() => {
    setSoundtrackBloomCue(true);
  }, []);
  const openBookingPrompt = useCallback(() => {
    setBookingPromptOpen(true);
  }, []);
  const closeBookingPrompt = useCallback(() => {
    setBookingPromptOpen(false);
  }, []);

  useEffect(() => {
    const scrollToHash = (hash, behavior = "smooth") => {
      if (!hash || hash === "#") {
        return;
      }

      const target = document.querySelector(hash);
      if (!target) {
        return;
      }

      const top =
        target.getBoundingClientRect().top + window.scrollY - 96;

      window.history.replaceState(null, "", hash);
      window.scrollTo({
        top: Math.max(top, 0),
        behavior,
      });
    };

    const handleAnchorClick = (event) => {
      const link = event.target.closest('a[href^="#"]');
      if (!link) {
        return;
      }

      const href = link.getAttribute("href");
      if (!href || href === "#") {
        return;
      }

      if (href === "#bookings" && !link.dataset.scrollOnly) {
        event.preventDefault();
        openBookingPrompt();
        return;
      }

      const target = document.querySelector(href);
      if (!target) {
        return;
      }

      event.preventDefault();
      scrollToHash(href);
    };

    document.addEventListener("click", handleAnchorClick);

    if (window.location.hash) {
      window.setTimeout(() => {
        scrollToHash(window.location.hash, "auto");
      }, 40);
    }

    return () => {
      document.removeEventListener("click", handleAnchorClick);
    };
  }, [openBookingPrompt]);

  useEffect(() => {
    const liteDevice = mobileSafeMode;

    if (!liteDevice) {
      void import("./components/PalaceHero");
      void import("./components/CaseStudies");
    }

    const warmRest = () => {
      if (liteDevice) {
        void import("./components/CaseStudies");
        void import("./components/RubhaaiSocial");
        void import("./components/ProductionDesign");
        void import("./components/VenueStageShowcase");
        void import("./components/HotelCollaborations");
        void import("./components/PerformanceTimeline");
        void import("./components/Worldwide");
        void import("./components/Gallery");
        void import("./components/Location");
        void import("./components/Footer");
        return;
      }

      void import("./components/CaseStudies");
      void import("./components/RubhaaiSocial");
      void import("./components/ProductionDesign");
      void import("./components/VenueStageShowcase");
      void import("./components/HotelCollaborations");
      void import("./components/PerformanceTimeline");
      void import("./components/Worldwide");
      void import("./components/Gallery");
      void import("./components/Location");
      void import("./components/Footer");
    };

    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(warmRest, { timeout: 1800 });
      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = window.setTimeout(warmRest, liteDevice ? 1800 : 1200);
    return () => window.clearTimeout(timeoutId);
  }, [mobileSafeMode]);

  return (
    <>
      {!loaded && (
        <IntroLoader
          onDone={handleIntroDone}
          onOpening={handleIntroOpening}
          onReveal={handleIntroReveal}
        />
      )}
      {loaded && (
        <>
          <div
            className="App grain bg-[#0a0a0a] text-white min-h-screen"
            data-testid="landing-page"
          >
            <Header />
            <main>
              {mobileSafeMode ? (
                <PalaceHeroLite />
              ) : (
                <Suspense fallback={<SectionFallback minHeight="100svh" />}>
                  <PalaceHero />
                </Suspense>
              )}
              <DeferredSection
                component={RubhaaiSocial}
                minHeight={860}
                rootMargin="900px 0px"
                anchorId="about"
              />
              <ArtistSpotlight />
              <Collection />
              <DeferredSection
                component={ProductionDesign}
                minHeight={720}
                rootMargin="900px 0px"
              />
              <DeferredSection
                component={VenueStageShowcase}
                minHeight={980}
                rootMargin="900px 0px"
              />
              <DeferredSection
                component={CaseStudies}
                minHeight={860}
                rootMargin="900px 0px"
              />
              <DeferredSection
                component={PerformanceTimeline}
                minHeight={680}
                rootMargin="900px 0px"
              />
              <DeferredSection
                component={HotelCollaborations}
                minHeight={620}
                rootMargin="850px 0px"
              />
              <DeferredSection
                component={Worldwide}
                minHeight={760}
                rootMargin="900px 0px"
                anchorId="worldwide"
              />
              <DeferredSection
                component={Gallery}
                minHeight={960}
                rootMargin="1000px 0px"
                anchorId="gallery"
              />
              <div id="bookings" className="scroll-mt-28">
                <Bookings />
              </div>
              <DeferredSection
                component={Location}
                minHeight={760}
                rootMargin="900px 0px"
              />
              <LeadershipSpotlight />
            </main>
            <DeferredSection
              component={Footer}
              minHeight={320}
              rootMargin="720px 0px"
            />
          </div>
          <SiteSoundtrack
            startCue={soundtrackStartCue}
            bloomCue={soundtrackBloomCue}
          />
          <BookingQuickPrompt
            open={bookingPromptOpen}
            onClose={closeBookingPrompt}
          />
          <WhatsAppFab />
        </>
      )}
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={SITE_PAUSED ? <SitePaused /> : <Landing />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
